import 'preact/devtools';
import React, { FC } from 'react';
import { SWRConfig, SWRConfiguration } from 'swr';
import { SystemNotifications } from '../SystemNotifications';
import '../styles/global.scss';
import { ProfilesLocalStorageProvider } from '../ProfilesLocalStorageContext';
import 'regenerator-runtime/runtime';
import { FeatureFlagProvider } from '../contexts/FeatureFlagContext/FeatureFlagContext';
import ErrorBoundary from '../error/ErrorBoundary';
import { AriaLiveProvider } from '../contexts/AriaLiveContext/AriaLiveContext';
import { SearchContextProvider } from '../contexts/SearchContext/SearchContext';
import {
  AppFeaturesContextProvider,
  Features,
} from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { BrazeProvider } from '../braze/BrazeProvider';
import { InputMethodContextProvider } from '../contexts/InputMethodContext/InputMethodContext';
import { AnalyticsContextProvider } from '../contexts/AnalyticsContext/AnalyticsContextProvider';
import { ViewContextProvider } from '../contexts/ViewContext/ViewContext';
import { AuthProvider } from '../contexts/AuthContext/AuthContext';
import { NotificationProvider } from '../contexts/NotificationContext/NotificationContext';
import { SidebarContextProvider } from '../contexts/SidebarContext/SidebarContext';
import Routes from '../Routes/Routes';
import { BrowserRouter } from 'react-router-dom';
import { render } from 'preact';
import { ContextMenuContextProvider } from '../contexts/ContextMenuContext/ContextMenuContext';

const globalSWRConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
  onError: (error: unknown) => {
    throw error;
  },
} as const satisfies SWRConfiguration;

/* Avoid nag logging for useLayoutEffect, which has been removed in recent versions of React
 * https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
 */
if (typeof window === 'undefined') React.useLayoutEffect = React.useEffect;

export type AppProps = {
  features: Features;
  featureBrazeEnabled: boolean;
};

export const renderApp = (
  rootElement: HTMLElement,
  features: Features,
  FEATURE_BRAZE: boolean
) => {
  render(
    <BrowserRouter>
      <App features={features} featureBrazeEnabled={FEATURE_BRAZE} />
    </BrowserRouter>,
    rootElement
  );
};

export const App: FC<AppProps> = ({ features, featureBrazeEnabled }) => {
  return (
    <AppFeaturesContextProvider {...features}>
      <ErrorBoundary>
        <SWRConfig value={globalSWRConfig}>
          <FeatureFlagProvider brazeEnabled={featureBrazeEnabled}>
            <ProfilesLocalStorageProvider>
              <AuthProvider>
                <BrazeProvider language={features.languageCode}>
                  <NotificationProvider>
                    <InputMethodContextProvider>
                      <SearchContextProvider>
                        <AriaLiveProvider>
                          <AnalyticsContextProvider>
                            <ViewContextProvider>
                              <SidebarContextProvider>
                                <ContextMenuContextProvider>
                                  <Routes />
                                </ContextMenuContextProvider>
                              </SidebarContextProvider>
                            </ViewContextProvider>
                          </AnalyticsContextProvider>
                        </AriaLiveProvider>
                      </SearchContextProvider>
                    </InputMethodContextProvider>
                    <SystemNotifications />
                  </NotificationProvider>
                </BrazeProvider>
              </AuthProvider>
            </ProfilesLocalStorageProvider>
          </FeatureFlagProvider>
        </SWRConfig>
      </ErrorBoundary>
    </AppFeaturesContextProvider>
  );
};

export default App;
